import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { language } = useLanguage();
  
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');
  const isSuccess = window.location.pathname.includes('success');

  useEffect(() => {
    if (!sessionId || !isSuccess) {
      setStatus(isSuccess ? 'loading' : 'cancelled');
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await api().get(`/payments/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('failed');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    pollStatus();
  }, [sessionId, isSuccess, attempts, api]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-caribbean/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="text-caribbean animate-spin" size={40} />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">
              {language === 'en' ? 'Processing Payment...' : 'Procesando Pago...'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'en' ? 'Please wait a moment' : 'Por favor espera un momento'}
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-caribbean/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-caribbean" size={40} />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2 text-caribbean">
              {language === 'en' ? 'Payment Successful!' : '¡Pago Exitoso!'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {language === 'en' 
                ? 'Your booking has been confirmed' 
                : 'Tu reserva ha sido confirmada'}
            </p>
            <Link to="/dashboard">
              <Button className="rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold px-8">
                {language === 'en' ? 'View My Bookings' : 'Ver Mis Reservas'}
              </Button>
            </Link>
          </div>
        );

      case 'cancelled':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="text-muted-foreground" size={40} />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">
              {language === 'en' ? 'Payment Cancelled' : 'Pago Cancelado'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {language === 'en' 
                ? 'Your payment was cancelled. No charges were made.' 
                : 'Tu pago fue cancelado. No se realizaron cargos.'}
            </p>
            <Link to="/dashboard">
              <Button className="rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold px-8">
                {language === 'en' ? 'Return to Dashboard' : 'Volver al Panel'}
              </Button>
            </Link>
          </div>
        );

      case 'failed':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="text-destructive" size={40} />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2 text-destructive">
              {language === 'en' ? 'Payment Failed' : 'Pago Fallido'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {language === 'en' 
                ? 'Something went wrong with your payment. Please try again.' 
                : 'Algo salió mal con tu pago. Por favor intenta de nuevo.'}
            </p>
            <Link to="/dashboard">
              <Button className="rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold px-8">
                {language === 'en' ? 'Try Again' : 'Intentar de Nuevo'}
              </Button>
            </Link>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="text-gold" size={40} />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">
              {language === 'en' ? 'Payment Pending' : 'Pago Pendiente'}
            </h1>
            <p className="text-muted-foreground mb-8">
              {language === 'en' 
                ? 'Your payment is being processed. Check your email for confirmation.' 
                : 'Tu pago está siendo procesado. Revisa tu email para confirmación.'}
            </p>
            <Link to="/dashboard">
              <Button className="rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold px-8">
                {language === 'en' ? 'Return to Dashboard' : 'Volver al Panel'}
              </Button>
            </Link>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center px-4" data-testid="payment-result-page">
      <div className="bg-card rounded-2xl shadow-xl p-8 max-w-md w-full">
        {renderContent()}
      </div>
    </div>
  );
}
